import React, {useContext, useMemo, useRef, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {v4 as uuidv4} from "uuid";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {getNeighborhoods, getStreets} from "../../../../../utils/location-utils.js";
import OptionsUtils from "../../../../../utils/options-utils.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import Button from "../../../../../components/button/button.jsx";
import {FaPen, FaPlus, FaTimes, FaTrash} from "react-icons/fa";
import {LocalizationAgGrid} from "../../../../../utils/localization-ag-grid.js";
import ImageCellRenderer from "../image-cell-renderer.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {CorporationContext} from "../../context/corporation-context.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import projectUtils from "../../../../../utils/project-utils.js";
import AlertMessages from "../../../../../components/messages/alert-message.js";
import {FaPencil} from "react-icons/fa6";
import aggridUtils from "../../../../../utils/aggrid-utils.js";

const API_URL = import.meta.env.VITE_API_URL;
const AlternativeHeaders = () => {
    const {state, setState, setChanges, alternativeHeaderRef} = useContext(CorporationContext)

    const initialState = {
        id: uuidv4(),
        name: "",
        address: "",
        number: "",
        neighborhood: "",
        imagePath: "",
        file: "",
        fileName: ""
    }

    const [open, setOpen] = useState(false);
    const [imagePath, setImagePath] = useState(undefined);
    const [newRow, setNewRow] = useState(false);
    const [modalState, setModalState] = useState(initialState);
    const imgRef = useRef();

    const onChange = ({target: {value, id}}) => handleInputs(value, id)

    const onChangeSelection = (id, value) => handleInputs(value, id);
    const handleInputs = (value, id) => {
        setModalState(prev => ({...prev, [id]: value}));
    }

    const openModal = (params) => {
        const {image_id, file} = params.data
        if (file) {
            setImagePath(URL.createObjectURL(file))
        } else {
            setImagePath(API_URL + (image_id ? `/attach/${image_id}` : "/corporation/entity/image"))
        }

        setModalState(params.data);
        setOpen(true)
    }

    const [columnDefs] = useState([
        {headerName: "", field: "image", width: 60,
            cellRenderer: ImageCellRenderer
        },
        {headerName: "Nome de exibição", field: "name", flex: 1,
            cellRenderer: ({value}) => value === -1 ? "Padrão" : value},
        {headerName: "Logradouro", field: "address", flex: 1},
        {headerName: "Número", field: "number", flex: 1},
        {headerName: "Bairro", field: "neighborhood", flex: 1},
        {
            headerName: "", field: "actions", width: 90,
            cellRenderer: (params) => {
                const data = params.data;
                if (data.id === -1) return;

                return <div className="flex gap-1 justify-center items-center h-full w-full">
                    <Button onClick={() => openModal(params)} className={"w-7.5! h-6.25! p-0!"}>
                        <FaPen />
                    </Button>
                    <Button target={"danger"} onClick={() => removeRow(params.data)} className={"w-7.5! h-6.25! p-0!"}>
                        <FaTrash />
                    </Button>
                </div>
            }
        },
    ]);

    const onHide = () => {
        setModalState(initialState);
        setImagePath("")
        setOpen(false)
        setNewRow(false)
    }

    const triggerInput = () => imgRef.current?.click();
    const attachImage = ({target: {files}}) => {
        const file = files[0];
        const reader = new FileReader();

        if (file.size >= 2000000) {
            let message = 'Tamanho máximo de arquivo excedido. Limite: 2MB.';
            projectUtils.swalErrorMessage('', message, 'Ok');
            return;
        }

        reader.onload = function (e) {
            if (e.target.result) {
                setImagePath(e.target.result);
                setModalState(prev => ({...prev, imagePath: e.target.result}));
            }
        }

        reader.readAsDataURL(file);
        setModalState(prev => ({...prev, file: file, fileName: file.name}));

        setChanges("alternative_headers");
    }

    const removeFile = () => {
        setImagePath("")
        setModalState(prev => ({...prev, imagePath: "", file: "", fileName: "", image_id: ""}));
    }

    const addRow = (data) => {
        setState(prev => ({
            ...prev,
            templates: [...state.templates, data]
        }))
        onHide()
    };

    const alterRow = (data) => {
        setState(prev => ({
            ...prev,
            templates: prev.templates.map(t => t.id === data.id ? data : t)
        }))

        setChanges("alternative_headers");
        onHide()
    }

    const removeRow = (data) => {
        setState(prev => ({
            ...prev,
            templates: prev.templates.filter(item => item.id !== data.id)
        }));
        onHide()
        setChanges("alternative_headers");
    };

    const newHeader = () => {
        setNewRow(true)
        setModalState(initialState)
        setOpen(true)
    }

    const handleSave = () => {
        if(canSave(modalState, newRow)){
            if (newRow) {
                setModalState(prev => ({...prev, id: uuidv4() }));
                addRow(modalState)
            }else{
                alterRow(modalState)
            }
        }
    }

    const canSave = (data, newRow) => {
        if (newRow){
            if (!data.file || !data.fileName){
                projectUtils.swalErrorMessage("", AlertMessages["missing.alternative.header.file"], "Ok");
                return false
            }
        }else{
            if (!data.image_id  && (!data.file || !data.fileName)){
                projectUtils.swalErrorMessage("", AlertMessages["missing.alternative.header.file"], "Ok");
                return false
            }
        }

        if (!data.name){
            projectUtils.swalErrorMessage("", AlertMessages["missing.exhibition.name"], "Ok");
            return false
        }

        return true
    }

    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    return (
        <>
            <Card>
                <Card.Header className={"gap-2"}>
                    <Card.Title>
                        Cabeçalhos alternativos para impressão
                    </Card.Title>
                    <Button className={"h-7.5! w-7.5 p-0!"} onClick={() => newHeader()} title={"Adicionar cabeçalho alternativo para impressão"}>
                        <FaPlus />
                    </Button>
                </Card.Header>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12">
                            <div className={"ag-theme-quartz"}>
                                <AgGridReact
                                    ref={alternativeHeaderRef}
                                    rowData={state.templates}
                                    columnDefs={columnDefs}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: LocalizationAgGrid}}
                                    domLayout='autoHeight'
                                    theme={theme}
                                />
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Modal open={open} setOpen={onHide} centered size="xl" backdrop="static" keyboard={false} title={"Cabeçalho alternativo"} overflow={true} className={"w-6/12"}>
                <Modal.Body className="p-2">
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-8">
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12">
                                    <InputForm id="name" value={modalState.name} label="Nome de exibição *"
                                              onChange={onChange}/>
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mt-3">
                                <div className="col-span-8">
                                    <SelectComponent
                                        loadOptions={getStreets}
                                        value={OptionsUtils.getValue(modalState.address)}
                                        isAsync={true}
                                        label={"Logradouro"}
                                        onChange={(selected) => onChangeSelection("address", selected?.value)}
                                        cacheOptions
                                        defaultOptions={[]}
                                    />
                                </div>
                                <div className="col-span-4">
                                    <InputForm id="number" value={modalState.number} label="Número"
                                              onChange={onChange}/>
                                </div>
                                <div className="col-span-12">
                                    <SelectComponent
                                        loadOptions={getNeighborhoods}
                                        value={OptionsUtils.getValue(modalState.neighborhood)}
                                        isAsync={true}
                                        label={"Bairro"}
                                        onChange={(selected) => onChangeSelection("neighborhood", selected?.value)}
                                        cacheOptions
                                        defaultOptions={[]}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="col-span-4">
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12">
                                    <div className="image-effective">
                                        <p className={"text-[14px]"}>Logo do Cabeçalho *</p>
                                        <div className="list-none flex gap-2 mb-0 h-[158px] border border-[#e0e0e0] rounded p-[5px] bg-center bg-contain bg-no-repeat cursor-pointer"
                                             style={{
                                                 backgroundImage: imagePath ? `url(${imagePath})` : '',
                                             }}
                                        >
                                            <Button className={"!w-[25px] !h-[25px] !p-0 absolute right-[10px]"} target={"danger"} onClick={() => removeFile()}  title="Remover imagem">
                                                <FaTimes/>
                                            </Button>
                                        </div>
                                        <input type="hidden" name="imageId" value={state.imageId ?? ""}/>
                                        <input type="file" accept="image/png, image/jpeg" className="hidden" name="image" onChange={attachImage}
                                               ref={imgRef}/>
                                        <Button className="w-full" onClick={triggerInput}>
                                            <span>Selecionar foto</span>
                                        </Button>
                                        <small>* Tamanho máximo 2MB</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className={"p-2"}>
                    <div className="w-full flex justify-end m-0">
                        <div className="flex gap-2">
                            <Button onClick={handleSave}>
                                Confirmar
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AlternativeHeaders;